import { ReactNode, useState, useEffect, CSSProperties } from "react";
import styled, { STANDARD_EASING } from "livemap-ui";
import classNames from "classnames";

interface SliderProps {
  step: number;
  children: ReactNode[];
  transitionDuration?: number;
}

type Props = SliderProps;

export function Slider({ children, step, transitionDuration = 440 }: Props) {
  const [stepElement, setStepElement] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);
  const [animatingHeight, setAnimatingHeight] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  /**
   * The current step cannot exceed the maximum step index,
   * whereas the parent-provided `step`-property can.
   */
  const currentStep = Math.min(step, children.length - 1);

  useEffect(() => setTransitioning(true), [currentStep]);
  useEffect(() => setTransitioning(false), []);

  useEffect(() => {
    if (transitioning) {
      setTimeout(() => setTransitioning(false), transitionDuration);
    }
  }, [transitioning, transitionDuration]);

  useEffect(() => {
    stepElement && setHeight(stepElement.clientHeight);
  }, [stepElement]);

  const getStepStyle = (index: number): CSSProperties => ({
    opacity: index === currentStep ? 1 : 0,
    left: `${100 * (index - currentStep)}%`,
    transitionDuration: transitionDuration + "ms",
  });

  const getStepClass = (index: number) =>
    classNames({
      active: index === currentStep,
      inactive: index !== currentStep,
    });

  const rootStyle: CSSProperties = {
    height,
    overflow: animatingHeight || transitioning ? "hidden" : "visible",
  };

  return (
    <Root
      style={rootStyle}
      className={classNames({ animatingHeight, transitioning })}
      onTransitionEnd={() => setAnimatingHeight(false)}
    >
      {children.map((child, i) => (
        <Step
          key={i}
          ref={i === currentStep ? setStepElement : undefined}
          className={getStepClass(i)}
          style={getStepStyle(i)}
        >
          {child}
        </Step>
      ))}
    </Root>
  );
}

const Step = styled.div`
  position: absolute;
  box-sizing: border-box;
`;

const Root = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  transition: height 600ms ${STANDARD_EASING};
  position: relative;

  > * {
    transition: left 400ms ${STANDARD_EASING}, opacity 200ms ease-in;
    width: 100%;
  }

  &.transitioning {
    overflow: hidden;
  }
`;
